import * as React from "react";
import {translate} from 'react-i18next';
import i18n from "i18next";
import {connect} from "react-redux";

// reducer
import {ITranslatedComponent, ITypefamilyMap} from "../../../redux/reducers/generalReducer";
import {ModelyearDto, TypefamilyDto, CnrDto, ResourceListOfModelyearDto} from "../../../services/Client";
import {EActiveMarketKey, PropertiesKey} from "../OptionsViewUtils";
import DependentSelect, {IDependentSelectDataAdapter, IDependentSelectFilter, EDirection} from "../../../components/DependentSelect";
import ColumnFactory from "../../../services/ColumnFactory";
import {Table} from "semantic-ui-react";
import deepEqual from 'deep-equal';
import {compose} from "redux";
import {selectBasicMarketCnr, selectUserPSLModelyears, selectUserTypefamiliesMap} from "../../../redux/selectors";
import {IReduxState} from "src/redux/IReduxState";

interface IMapStateToProps {
    userPSLModelyears: ResourceListOfModelyearDto;
    basicMarketCnr: CnrDto | undefined;
    userTypefamiliesMap: ITypefamilyMap;
}
export interface ICopyPSL {
    sourceModelyear?: ModelyearDto;
    sourceTypefamily?: TypefamilyDto;
    sourceCnr?: CnrDto;

    targetModelyear?: ModelyearDto;
    targetTypefamily?: TypefamilyDto;
    targetCnr?: CnrDto;
}

interface OptionCopy extends IDependentSelectFilter, ICopyPSL { }

type TypesForAdapter = ModelyearDto | TypefamilyDto | CnrDto;
class Adapter<TDto extends TypesForAdapter> implements IDependentSelectDataAdapter {
    constructor(private adapter: PslCopy, private key: string) {
    }
    getKey(): string {
        return this.key;
    }
    getData(): TDto[] {
        return this.adapter.getData(this.getKey()) as TDto[];
    }
    getFilter(): IDependentSelectFilter {
        return this.adapter.getFilter();
    }
    setFilter(filter: IDependentSelectFilter): Promise<void> {
        return this.adapter.setFilter(filter);
    }
}

class MJA extends Adapter<ModelyearDto> {
    renderer = (dto: ModelyearDto) => ColumnFactory.mj2str(dto);
}

class TFA extends Adapter<TypefamilyDto> {
    renderer = (dto: TypefamilyDto) => i18n.t("GLOBAL:" + dto.typefamily);
}

class CNA extends Adapter<CnrDto> {
    renderer = (dto: CnrDto) => dto.name;
}

interface IPslCopyProps extends IMapStateToProps, ITranslatedComponent {
    value?: ICopyPSL;
    onChange?(copyPSL: ICopyPSL): void;
    userPSLModelyears: ResourceListOfModelyearDto;
    basicMarketCnr: CnrDto;
    userTypefamiliesMap: ITypefamilyMap;
}

interface IPslCopyState {
    dataAdaptersSrc: Array<IDependentSelectDataAdapter>;
    dataAdaptersTgt: Array<IDependentSelectDataAdapter>;
    curSelection: OptionCopy;
}

class PslCopy extends React.Component<IPslCopyProps, IPslCopyState> {
    constructor(props: IPslCopyProps) {
        super(props);

        this.state = {
            dataAdaptersSrc: [new CNA(this, PropertiesKey.SourceCnrKey), new MJA(this, "sourceModelyear"), new TFA(this, "sourceTypefamily")],
            dataAdaptersTgt: [new CNA(this, PropertiesKey.TargetCnrKey), new MJA(this, "targetModelyear"), new TFA(this, "targetTypefamily")],
            curSelection: props.value ? props.value as OptionCopy : {}
        };

        this.getData = this.getData.bind(this);
        this.getFilter = this.getFilter.bind(this);
        this.setFilter = this.setFilter.bind(this);
        this.getTypefamily = this.getTypefamily.bind(this);
    }

    componentWillReceiveProps(nextProps: Readonly<IPslCopyProps>) {
        if (!deepEqual(nextProps.value, this.props.value, {strict: true})) {
            this.setFilter(nextProps.value ? nextProps.value as OptionCopy : {});
        }
    }

    render() {
        const {t} = this.props;
        const placeholders = [
            t("C_NUMBER"),
            t("MODELYEAR"),
            t("TYPEFAMILY")
        ];

        const {dataAdaptersSrc, dataAdaptersTgt, curSelection} = this.state;
        const negSrc = !curSelection.sourceCnr || !curSelection.sourceModelyear || !curSelection.sourceTypefamily;
        const negTgt = !curSelection.targetCnr || !curSelection.targetModelyear || !curSelection.targetTypefamily;

        return (
            <Table>
                <Table.Body>
                    <Table.Row>
                        <Table.Cell width="1" negative={negSrc}>
                            <DependentSelect
                                title={t("SOURCE")}
                                titleStyle={{textAlign: 'left', fontWeight: 'normal'}}
                                direction={EDirection.Vertical}
                                placeholders={placeholders}
                                dataAdapters={dataAdaptersSrc}
                                value={curSelection}
                                boldFeature={true}
                                activeMarket={EActiveMarketKey.BasicMarketActiveKey}
                                invisibleCnr={true}
                            />
                        </Table.Cell>
                        <Table.Cell width="1" negative={negTgt}>
                            <DependentSelect
                                title={t("TARGET")}
                                titleStyle={{textAlign: 'left', fontWeight: 'normal'}}
                                readonlies={[true, curSelection.sourceModelyear === undefined, true]}
                                direction={EDirection.Vertical}
                                placeholders={placeholders}
                                dataAdapters={dataAdaptersTgt}
                                value={curSelection}
                                boldFeature={true}
                                activeMarket={EActiveMarketKey.BasicMarketActiveKey}
                                invisibleCnr={true}
                            />
                        </Table.Cell>
                    </Table.Row>
                </Table.Body>
            </Table>
        );
    }

    getData(key: string): TypesForAdapter[] {
        const {userPSLModelyears, basicMarketCnr} = this.props;
        const {curSelection} = this.state;

        const typefamilySource = this.getTypefamily(curSelection.sourceModelyear);

        let result: TypesForAdapter[];
        switch (key) {
            case "sourceModelyear":
                result = userPSLModelyears.elements ? userPSLModelyears.elements : [];
                break;
            case "targetModelyear":
                result = curSelection.sourceModelyear !== undefined && userPSLModelyears.elements
                    ? userPSLModelyears.elements.filter(value => value.mj1 !== curSelection.sourceModelyear!.mj1) : [];
                break;
            case "sourceTypefamily":
                result = typefamilySource;
                break;
            case "targetTypefamily":
                result = curSelection.targetTypefamily ? [curSelection.targetTypefamily] : [];
                break;
            case PropertiesKey.SourceCnrKey:
            case PropertiesKey.TargetCnrKey:
                result = basicMarketCnr ? [basicMarketCnr] : [];
                break;
            default:
                result = [];
                break;
        }
        return result;
    }
    getFilter(): IDependentSelectFilter {
        return this.state.curSelection;
    }
    setFilter(filter: OptionCopy): Promise<void> {
        const {userPSLModelyears} = this.props;
        const sel = Object.assign({}, this.state.curSelection, filter);

        if (sel.sourceModelyear !== undefined && userPSLModelyears.elements) {
            sel.sourceModelyear = userPSLModelyears.elements.find(value => value.mj1 === sel.sourceModelyear!.mj1);
        }
        else {
            sel.sourceModelyear = undefined;
        }

        if (sel.sourceModelyear !== undefined && sel.targetModelyear !== undefined && sel.sourceModelyear.mj1 === sel.targetModelyear.mj1) {
            sel.targetModelyear = undefined;
        }
        if (sel.sourceModelyear !== undefined && sel.sourceTypefamily !== undefined) {
            const typefamilySource = this.getTypefamily(sel.sourceModelyear);
            sel.sourceTypefamily = typefamilySource.find(value => value.typefamily === sel.sourceTypefamily!.typefamily);
        }
        if (sel.sourceModelyear !== undefined && sel.targetModelyear !== undefined && sel.sourceTypefamily !== undefined) {
            const typefamilyTarget = this.getTypefamily(sel.targetModelyear);
            sel.targetTypefamily = typefamilyTarget.find(value => value.typefamily === sel.sourceTypefamily!.typefamily);
        }
        else {
            sel.targetTypefamily = undefined;
        }

        this.setState({curSelection: sel}, () => {
            if (this.props.onChange) {
                this.props.onChange(sel);
            }
        });
        return Promise.resolve();
    }

    getTypefamily(modelyear: ModelyearDto | undefined): TypefamilyDto[] {
        const typefamilyRes = modelyear ? this.props.userTypefamiliesMap[modelyear.mj1] : undefined;
        return typefamilyRes && typefamilyRes.elements ? typefamilyRes.elements : [];
    }
}

const mapStateToProps = (state: IReduxState): IMapStateToProps => ({
    userPSLModelyears: selectUserPSLModelyears(state),
    basicMarketCnr: selectBasicMarketCnr(state),
    userTypefamiliesMap: selectUserTypefamiliesMap(state)
});

export default compose<React.ComponentType<any>>(
    translate("OPTIONS_VIEW"),
    connect<IMapStateToProps>(mapStateToProps)
)(PslCopy);



// WEBPACK FOOTER //
// ./src/views/OptionsView/PSL/PslCopy.tsx